// === SAA:UTILS:SUBSCRIPTION-FEE:v1 ===
// Monthly subscription fee ("Serviceabonnement") for SAA. The amount is tiered by the number of
// selected products and is charged every month. Unlike the insurance premium (21%
// assurantiebelasting), this broker fee carries a *different* tax rate (5%), so it is tracked
// and displayed on its own tax line rather than folded into the 21% total.
//
// Config (per SAA brand config.*.json):
//   "subscriptionFee": { "enabled": true, "tier1": 12.50, "tier3": 20.00, "taxRate": 0.05 }
//   tier1 = up to 2 products, tier3 = 3+ products.

type ProductSelection = {
  personenauto?: boolean;
  woonhuis?: boolean;
  inboedel?: boolean;
  aansprakelijkheid?: boolean;
  rechtsbijstand?: boolean;
  ongevallen?: boolean;
  doorlopendereis?: boolean;
};

export interface SubscriptionFeeConfig {
  enabled?: boolean;
  tier1?: number;
  tier3?: number;
  taxRate?: number;
}

export interface SubscriptionFeeResult {
  net: number;      // ex-tax monthly fee (the configured tier value)
  tax: number;      // taxRate applied to net, rounded to 2 decimals
  withTax: number;  // net + tax — the amount actually charged/displayed
  taxRate: number;  // rate applied, e.g. 0.05 (used for the "5%" label)
}

const round2 = (n: number) => Math.round(n * 100) / 100;

const countSelected = (selectedProducts: ProductSelection | null | undefined): number =>
  [
    selectedProducts?.personenauto,
    selectedProducts?.woonhuis,
    selectedProducts?.inboedel,
    selectedProducts?.aansprakelijkheid,
    selectedProducts?.rechtsbijstand,
    selectedProducts?.ongevallen,
    selectedProducts?.doorlopendereis,
  ].filter(Boolean).length;

/**
 * Compute the monthly subscription fee with its own tax level.
 * Returns all-zero (and taxRate 0) when the fee is disabled, so callers can add the values
 * unconditionally.
 */
export const computeSubscriptionFee = (
  selectedProducts: ProductSelection | null | undefined,
  config: any,
): SubscriptionFeeResult => {
  const cfg: SubscriptionFeeConfig | undefined = config?.subscriptionFee;
  if (!cfg?.enabled) return { net: 0, tax: 0, withTax: 0, taxRate: 0 };

  const net = countSelected(selectedProducts) > 2 ? (cfg.tier3 ?? 20.0) : (cfg.tier1 ?? 12.5);
  const taxRate = cfg.taxRate ?? 0.05;
  const tax = round2(net * taxRate);
  return { net: round2(net), tax, withTax: round2(net + tax), taxRate };
};

/**
 * Entry price of the sidebar SubscriptionCard ("Vanaf € X per maand"): the 1-2 product tier
 * INCLUDING its tax, i.e. the exact amount PremiumBreakdownCard's Serviceabonnement row shows
 * for those products. Both cards share the screen, so they must agree.
 */
export const subscriptionEntryFee = (config: any): number => computeSubscriptionFee({}, config).withTax;

/** Whole-number percent for labels, e.g. 0.05 -> "5". */
export const subscriptionTaxPercentLabel = (taxRate: number): string =>
  String(Math.round(taxRate * 100));
